/* @jsxImportSource react */
'use client';

import { createContext, type ReactNode, type RefObject, useContext } from 'react';

/**
 * Portal container type - matches Base UI's FloatingPortal container prop.
 * Supports HTMLElement, ShadowRoot, or a ref to either.
 */
export type PortalContainer =
  | HTMLElement
  | ShadowRoot
  | null
  | RefObject<HTMLElement | ShadowRoot | null>;

const PortalContainerContext = createContext<PortalContainer>(null);

/**
 * AtomsPortalProvider — sets the default portal container for all Atoms overlay components.
 *
 * Use this to render Atoms overlays (Dialog, Combobox,
 * CommandPalette, Toast) inside a Shadow DOM or custom container.
 *
 * When not provided, overlays portal to `document.body` (default browser behavior).
 * Individual components can override this via their own `container` prop.
 *
 * @example Shadow DOM usage
 * ```tsx
 * function WebComponent() {
 *   const shadowRef = useRef<ShadowRoot>(null);
 *
 *   useEffect(() => {
 *     shadowRef.current = hostElement.attachShadow({ mode: 'open' });
 *   }, []);
 *
 *   return (
 *     <AtomsPortalProvider container={shadowRef.current}>
 *       <App />
 *     </AtomsPortalProvider>
 *   );
 * }
 * ```
 *
 * @example Custom container
 * ```tsx
 * const containerRef = useRef<HTMLDivElement>(null);
 *
 * <div ref={containerRef}>
 *   <AtomsPortalProvider container={containerRef}>
 *     <Dialog.Root>...</Dialog.Root>
 *   </AtomsPortalProvider>
 * </div>
 * ```
 */
export function AtomsPortalProvider({
  container,
  children,
}: {
  /** The container element or ShadowRoot to portal overlays into. */
  container: PortalContainer;
  children: ReactNode;
}) {
  return (
    <PortalContainerContext.Provider value={container}>{children}</PortalContainerContext.Provider>
  );
}

/**
 * Hook to get the portal container from context.
 * Returns null if no provider is present (defaults to document.body).
 *
 * Used by overlay components to resolve their portal container; not part of the
 * package's public surface.
 */
export function usePortalContainer(): PortalContainer {
  return useContext(PortalContainerContext);
}
